import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Check, X, Zap, Crown, Star, Gift, Clock } from 'lucide-react';
import { useUserData } from '../contexts/UserContext';

const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || '';
const PAYPAL_PLAN_BASIC = import.meta.env.VITE_PAYPAL_PLAN_BASIC || '';
const PAYPAL_PLAN_ELITE = import.meta.env.VITE_PAYPAL_PLAN_ELITE || '';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: PlanFeature[];
  planId: string;
  tier: 'basic' | 'elite';
  popular?: boolean;
  icon: React.ReactNode;
  color: string;
}

const plans: Plan[] = [
  {
    name: 'Basic',
    price: '£19.99',
    period: '/month',
    description: 'Perfect for active traders getting started',
    tier: 'basic',
    planId: PAYPAL_PLAN_BASIC,
    icon: <Zap className="w-6 h-6" />,
    color: 'cyan',
    features: [
      { text: '5 scans per day', included: true },
      { text: '7-Step Analysis Framework', included: true },
      { text: 'Real-time market data', included: true },
      { text: 'Position sizing calculator', included: true },
      { text: 'Trade plan generation', included: true },
      { text: 'Basic watchlist (10 tickers)', included: true },
      { text: 'Elite trade management advice', included: false },
      { text: 'Priority support', included: false },
    ],
  },
  {
    name: 'Elite',
    price: '£29.99',
    period: '/month',
    description: 'For serious traders who want the edge',
    tier: 'elite',
    planId: PAYPAL_PLAN_ELITE,
    popular: true,
    icon: <Crown className="w-6 h-6" />,
    color: 'amber',
    features: [
      { text: '50 scans per day', included: true },
      { text: '7-Step Analysis Framework', included: true },
      { text: 'Real-time market data', included: true },
      { text: 'Position sizing calculator', included: true },
      { text: 'Trade plan generation', included: true },
      { text: 'Extended watchlist (50 tickers)', included: true },
      { text: 'Elite trade management advice', included: true },
      { text: 'Priority support', included: true },
    ],
  },
];

export const PricingPage: React.FC = () => {
  const { isSignedIn, user } = useUser();
  const { userData, refreshUser, applyPromoCode, startFreeTrial } = useUserData();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [trialMessage, setTrialMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [startingTrial, setStartingTrial] = useState(false);

  const handleStartTrial = async () => {
    setStartingTrial(true);
    setTrialMessage(null);

    const result = await startFreeTrial();
    setTrialMessage({
      type: result.success ? 'success' : 'error',
      text: result.message,
    });

    if (result.success) {
      setTimeout(() => navigate('/scanner'), 2000);
    }
    setStartingTrial(false);
  };

  const handlePromoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoCode.trim()) return;

    const result = await applyPromoCode(promoCode);
    setPromoMessage({
      type: result.success ? 'success' : 'error',
      text: result.message,
    });

    if (result.success) {
      setPromoCode('');
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  const handleSubscriptionSuccess = async (subscriptionId: string, tier: string, amount: string) => {
    if (!user) return;

    try {
      // Activate subscription in our backend
      const res = await fetch('/api/subscription?action=activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          subscriptionId,
          tier,
          amount,
        }),
      });

      const data = await res.json();

      if (data.success) {
        // Send confirmation email
        await fetch('/api/email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'subscription-confirmed',
            email: user.primaryEmailAddress?.emailAddress,
            name: user.firstName || user.fullName,
            tier,
            amount,
          }),
        });

        await refreshUser();
        navigate('/dashboard');
      } else {
        setError(data.error || 'Failed to activate subscription');
      }
    } catch (err) {
      setError('Failed to process subscription');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="min-h-screen bg-terminal-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                  7S
                </div>
              </div>
              <span className="text-terminal-bright font-bold tracking-tight text-lg">
                ALPHA<span className="text-cyan-400">OPTIONS</span>
              </span>
            </Link>

            <div className="flex items-center gap-4">
              {isSignedIn ? (
                <Link to="/dashboard" className="text-terminal-dim hover:text-white text-sm">
                  Dashboard
                </Link>
              ) : (
                <Link to="/login" className="text-terminal-dim hover:text-white text-sm">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">Trading Edge</span>
          </h1>
          <p className="text-terminal-dim text-lg max-w-2xl mx-auto">
            Unlock institutional-grade options analysis. Cancel anytime.
          </p>
        </div>

        {/* Current Plan Badge */}
        {userData && userData.effectiveTier !== 'free' && (
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400">
              <Check className="w-4 h-4" />
              Current Plan: {userData.effectiveTier.toUpperCase()}
              {userData.hasValidPromo && ' (Promo)'}
              {userData.hasValidTrial && ` - ${userData.trialDaysRemaining} days left`}
            </span>
          </div>
        )}

        {/* Free Trial Section */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-2xl border border-emerald-500/30 p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-4">
              <Gift className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Try Free for 7 Days</h2>
            <p className="text-terminal-dim mb-6 max-w-md mx-auto">
              Get full access to our Basic plan features. No credit card required.
              5 scans per day for 7 days.
            </p>

            {!isSignedIn ? (
              <SignInButton mode="modal">
                <button className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-black rounded-lg font-bold hover:opacity-90 transition-opacity">
                  <Gift className="w-5 h-5" />
                  Sign Up for Free Trial
                </button>
              </SignInButton>
            ) : userData?.hasValidTrial ? (
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-lg text-emerald-400">
                  <Clock className="w-4 h-4" />
                  Trial Active - {userData.trialDaysRemaining} days remaining
                </div>
                <div>
                  <Link
                    to="/scanner"
                    className="inline-flex items-center gap-2 px-6 py-2 bg-emerald-500 text-black rounded-lg font-bold hover:opacity-90"
                  >
                    Open Scanner
                  </Link>
                </div>
              </div>
            ) : userData?.hasUsedTrial ? (
              <div className="text-terminal-dim">
                <p className="mb-4">You've already used your free trial.</p>
                <p className="text-sm">Subscribe to a plan below to continue scanning!</p>
              </div>
            ) : userData?.canStartTrial ? (
              <div className="space-y-3">
                <button
                  onClick={handleStartTrial}
                  disabled={startingTrial}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-black rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {startingTrial ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Starting Trial...
                    </>
                  ) : (
                    <>
                      <Gift className="w-5 h-5" />
                      Start Free 7-Day Trial
                    </>
                  )}
                </button>

                {trialMessage && (
                  <div className={`p-3 rounded-lg ${
                    trialMessage.type === 'success'
                      ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                      : 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
                  }`}>
                    {trialMessage.text}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-terminal-dim">
                You already have an active subscription!
              </p>
            )}
          </div>
        </div>

        <div className="text-center mb-8">
          <p className="text-terminal-dim">Or subscribe for unlimited access</p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          {plans.map((plan) => (
            <div
              key={plan.tier}
              className={`relative bg-terminal-card rounded-2xl border ${
                plan.popular ? 'border-amber-500/50' : 'border-terminal-border'
              } p-8 ${plan.popular ? 'ring-2 ring-amber-500/20' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-black text-xs font-bold rounded-full">
                    <Star className="w-3 h-3" /> MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-${plan.color}-500/10 text-${plan.color}-400 mb-4`}>
                {plan.icon}
              </div>

              <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
              <p className="text-terminal-dim text-base mb-4">{plan.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-black text-white">{plan.price}</span>
                <span className="text-terminal-dim">{plan.period}</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
                    )}
                    <span className={feature.included ? 'text-terminal-text' : 'text-slate-300'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Subscribe Button */}
              {isSignedIn ? (
                userData?.effectiveTier === plan.tier ? (
                  <button
                    disabled
                    className="w-full py-3 px-4 bg-emerald-500/20 text-emerald-400 rounded-lg font-bold cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, vault: true, intent: 'subscription' }}>
                    <PayPalButtons
                      style={{ layout: 'horizontal', color: 'gold', shape: 'rect', label: 'subscribe' }}
                      createSubscription={(data, actions) => {
                        setProcessing(plan.tier);
                        return actions.subscription.create({
                          plan_id: plan.planId,
                        });
                      }}
                      onApprove={async (data) => {
                        await handleSubscriptionSuccess(data.subscriptionID!, plan.tier, plan.price);
                      }}
                      onError={(err) => {
                        console.error('PayPal error:', err);
                        setError('Payment failed. Please try again.');
                        setProcessing(null);
                      }}
                      onCancel={() => {
                        setProcessing(null);
                      }}
                    />
                  </PayPalScriptProvider>
                )
              ) : (
                <SignInButton mode="modal">
                  <button className={`w-full py-3 px-4 bg-gradient-to-r ${
                    plan.popular ? 'from-amber-500 to-amber-600' : 'from-cyan-500 to-emerald-500'
                  } text-black rounded-lg font-bold hover:opacity-90 transition-opacity`}>
                    Sign Up to Subscribe
                  </button>
                </SignInButton>
              )}
            </div>
          ))}
        </div>

        {/* Promo Code Section */}
        <div className="max-w-md mx-auto mb-16">
          <div className="bg-terminal-card rounded-xl border border-terminal-border p-6">
            <h3 className="text-lg font-bold text-white mb-4 text-center">Have a Promo Code?</h3>

            {!isSignedIn ? (
              <p className="text-terminal-dim text-center text-sm">
                <Link to="/login" className="text-cyan-400 hover:text-cyan-300">Sign in</Link> to apply a promo code
              </p>
            ) : (
              <form onSubmit={handlePromoSubmit} className="space-y-4">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="w-full px-4 py-3 bg-slate-900 border border-terminal-border rounded-lg text-white placeholder-terminal-dim focus:border-cyan-500 focus:outline-none font-mono"
                />
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-slate-800 border border-terminal-border text-white rounded-lg font-bold hover:bg-slate-700 transition-colors"
                >
                  Apply Code
                </button>

                {promoMessage && (
                  <div className={`p-3 rounded-lg text-center ${
                    promoMessage.type === 'success'
                      ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                      : 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
                  }`}>
                    {promoMessage.text}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="max-w-md mx-auto mb-8">
            <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-400 text-center">
              {error}
            </div>
          </div>
        )}

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>

          <div className="space-y-4">
            <div className="bg-terminal-card rounded-lg border border-terminal-border p-6">
              <h3 className="text-white font-bold mb-2">Can I cancel anytime?</h3>
              <p className="text-terminal-dim">Yes, you can cancel your subscription at any time from your dashboard. You'll continue to have access until the end of your billing period.</p>
            </div>

            <div className="bg-terminal-card rounded-lg border border-terminal-border p-6">
              <h3 className="text-white font-bold mb-2">What payment methods do you accept?</h3>
              <p className="text-terminal-dim">We accept PayPal and all major credit/debit cards through PayPal's secure payment system.</p>
            </div>

            <div className="bg-terminal-card rounded-lg border border-terminal-border p-6">
              <h3 className="text-white font-bold mb-2">What is "Elite trade management advice"?</h3>
              <p className="text-terminal-dim">Elite subscribers get additional guidance on managing open positions, including when to take profits, roll positions, or cut losses. Note: This is educational content, not financial advice.</p>
            </div>

            <div className="bg-terminal-card rounded-lg border border-terminal-border p-6">
              <h3 className="text-white font-bold mb-2">When do my daily scans reset?</h3>
              <p className="text-terminal-dim">Your scan count resets at midnight UTC each day.</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-16 text-center text-terminal-dim text-sm max-w-2xl mx-auto">
          <p>
            Alpha Options is an educational tool for options analysis. The information provided is not financial advice.
            Trading options involves substantial risk of loss and is not suitable for all investors.
            Past performance does not guarantee future results.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
